/* V9 catalogue UI enhancement. The existing theme.LoadFilterer owns filtering,
   sorting, history, product-image lifecycle and AJAX replacement. No extra fetches. */
(() => {
  'use strict';
  const selector = '.ab-v9-catalogue[data-section-type="collection-template"]';
  const controllers = new Map();
  const initializedVideos = new WeakSet();
  const initializedFallbacks = new WeakSet();
  const openDesktop = 'filter-container--show-filters-desktop';
  const openMobile = 'filter-container--show-filters-mobile';

  function init(root) {
    if (controllers.has(root)) return;
    let previousOpen = false;
    let previousDrawer = null;
    let openerId = '';
    let focusId = '';
    let queued = false;
    let destroyed = false;
    const inertElements = new Map();
    const compactGroupState = new Map();
    const compactGroupsInitialized = new WeakSet();
    const compactMobile = window.matchMedia('(max-width: 767.98px)');
    const events = new AbortController();
    const signal = events.signal;

    function drawer() { return root.querySelector('.filter-container > .filters'); }
    function container() { return root.querySelector('.filter-container'); }
    function isOpen() {
      const el = container();
      return !!el && (el.classList.contains(openDesktop) || el.classList.contains(openMobile));
    }
    function focusables(panel) {
      return Array.from(panel.querySelectorAll('button, a[href], input, select, textarea, [tabindex]'))
        .filter(el => !el.disabled && el.tabIndex >= 0 && !el.closest('[inert]') && el.getClientRects().length);
    }
    function releaseBackground() {
      inertElements.forEach((wasInert, el) => { el.inert = wasInert; });
      inertElements.clear();
    }
    function lockBackground(panel) {
      // Keep all ancestors in normal flow; inert only sibling branches outside the dialog.
      let branch = panel;
      while (branch && branch !== document.body) {
        const parent = branch.parentElement;
        if (!parent) break;
        Array.from(parent.children).forEach(el => {
          if (el === branch || el.classList.contains('filter-shade') || /^(SCRIPT|STYLE|LINK)$/.test(el.tagName)) return;
          if (!inertElements.has(el)) inertElements.set(el, el.inert);
          el.inert = true;
        });
        branch = parent;
      }
    }
    function close() {
      if (!isOpen()) return;
      const button = root.querySelector('.filters__close[data-toggle-filters]');
      if (button) button.click(); // Use the native toggle handler, not a second controller.
    }
    function syncVideos() {
      root.querySelectorAll('.ab-collection-pick video').forEach(video => {
        if (initializedVideos.has(video)) return;
        initializedVideos.add(video);
        const tile = video.closest('.ab-collection-pick');
        const button = tile.querySelector('[data-ab-pick-video-toggle]');
        if (!button) return;
        const update = () => {
          const playing = !video.paused && !video.ended;
          const label = playing ? 'Pause video' : 'Play video';
          button.textContent = label;
          button.setAttribute('aria-label', `${label.replace(' video', '')} Our Pick video`);
        };
        video.addEventListener('play', update);
        video.addEventListener('pause', update);
        video.addEventListener('ended', update);
        video.addEventListener('error', () => {
          video.pause();
          button.disabled = true;
          button.textContent = 'Video unavailable';
          button.setAttribute('aria-label', 'Our Pick video is unavailable');
          tile.dataset.abPickMedia = 'unavailable';
          if (video.poster) {
            const image = document.createElement('img');
            image.src = video.poster;
            image.alt = 'Our Pick image fallback';
            video.hidden = true;
            video.style.display = 'none';
            video.after(image);
            tile.dataset.abPickMedia = 'image';
          }
        }, { once: true });
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) video.pause();
        update();
      });
    }
    function compactFilterGroups(panel) {
      if (!compactMobile.matches) return;
      const form = panel.querySelector('#CollectionFilterForm');
      if (!form) return;
      const priority = {price:0, availability:1, colour:2, category:3, size:4, collection:5, other:6, sort:7};
      const groups = Array.from(form.children).filter(el => el.classList.contains('filter-group'));
      groups.forEach(group => {
        const heading = group.querySelector('.filter-group__heading[data-toggle-target]');
        const label = group.querySelector('.filter-group__heading__text');
        const text = (label?.textContent || '').trim().toLowerCase();
        const names = Array.from(group.querySelectorAll('input[name]')).map(el => el.name);
        let kind = 'other';
        if (group.classList.contains('filter-group--availability') || names.includes('filter.v.availability')) kind = 'availability';
        else if (group.querySelector('.cc-price-range')) kind = 'price';
        else if (/colou?r/.test(text)) kind = 'colour';
        else if (/categor|product type/.test(text) || names.includes('filter.p.product_type') || names.some(name => name === 'filter.p.category')) kind = 'category';
        else if (/size/.test(text)) kind = 'size';
        else if (/collection/.test(text)) kind = 'collection';
        else if (names.includes('sort_by')) kind = 'sort';
        group.dataset.abFilterKind = kind;
        if (label && ['colour','category','size','collection'].includes(kind)) {
          const normalized = kind[0].toUpperCase()+kind.slice(1);
          if (label.textContent !== normalized) label.textContent = normalized;
        }
        if (!heading) return;
        const target = panel.querySelector(heading.getAttribute('data-toggle-target'));
        if (!target) return;
        const key = names[0] || text;
        if (!compactGroupsInitialized.has(group)) {
          compactGroupsInitialized.add(group);
          const expanded = compactGroupState.has(key) ? compactGroupState.get(key) : kind === 'price';
          target.classList.toggle('toggle-target--hidden', !expanded);
          heading.classList.toggle('toggle-target-toggler--is-hidden', !expanded);
          heading.setAttribute('aria-expanded', String(expanded));
          target.style.height = '';
          compactGroupState.set(key, expanded);
        } else {
          compactGroupState.set(key, !target.classList.contains('toggle-target--hidden'));
        }
      });
      const sorted = [...groups].sort((a,b) => priority[a.dataset.abFilterKind]-priority[b.dataset.abFilterKind]);
      if (groups.some((group,index) => group !== sorted[index])) {
        const fragment = document.createDocumentFragment();
        sorted.forEach(group => fragment.appendChild(group));
        form.insertBefore(fragment, form.querySelector('.ab-filter-drawer__submit') || null);
      }
    }
    function sync() {
      queued = false;
      if (destroyed || !root.isConnected) return;
      const panel = drawer();
      if (!panel) return;
      compactFilterGroups(panel);
      const open = isOpen();
      const busy = !!root.querySelector('[data-ajax-container].ajax-loading');
      panel.inert = !open;
      panel.setAttribute('aria-hidden', String(!open));
      panel.setAttribute('aria-busy', String(busy));
      root.querySelectorAll('.utility-bar .toggle-btn[data-toggle-filters]').forEach(button => {
        button.setAttribute('aria-expanded', String(open));
        button.setAttribute('aria-controls', panel.id);
      });
      root.querySelectorAll('[data-ab-fallback-filter]').forEach(input => {
        if (initializedFallbacks.has(input)) return;
        input.checked = new URLSearchParams(window.location.search).getAll(input.name).includes(input.value);
        initializedFallbacks.add(input);
      });
      root.querySelectorAll('.filters [data-toggle-target]').forEach(button => {
        const groupSelector = button.getAttribute('data-toggle-target');
        const group = panel.querySelector(groupSelector);
        if (group) button.setAttribute('aria-expanded', String(!group.classList.contains('toggle-target--hidden')));
      });
      if (open) {
        lockBackground(panel);
        document.body.classList.add('ab-v9-filters-open');
        if (!previousOpen || previousDrawer !== panel) {
          const active = document.activeElement;
          if (!panel.contains(active)) {
            const remembered = focusId && document.getElementById(focusId);
            const target = remembered && panel.contains(remembered) ? remembered : panel.querySelector('.filters__close') || panel;
            target.focus({ preventScroll: true });
          }
        }
      } else if (previousOpen) {
        releaseBackground();
        document.body.classList.remove('ab-v9-filters-open');
        const oldOpener = openerId && document.getElementById(openerId);
        const trigger = oldOpener && oldOpener.getClientRects().length ? oldOpener : root.querySelector('.utility-bar:not(.utility-bar--sticky-mobile-copy) [data-toggle-filters]');
        if (trigger) trigger.focus({ preventScroll: true });
        focusId = '';
      }
      previousOpen = open;
      previousDrawer = panel;
      syncVideos();
    }
    function scheduleSync() {
      if (queued || destroyed) return;
      queued = true;
      queueMicrotask(sync);
    }

    root.addEventListener('click', event => {
      const toggle = event.target.closest('[data-toggle-filters]');
      if (toggle && !isOpen() && toggle.closest('.utility-bar')) {
        openerId = toggle.id;
        focusId = '';
      }
      const videoButton = event.target.closest('[data-ab-pick-video-toggle]');
      if (videoButton) {
        const video = videoButton.closest('.ab-collection-pick').querySelector('video');
        if (video && !videoButton.disabled) {
          if (video.paused) {
            video.play().catch(() => {
              videoButton.textContent = 'Play video';
              videoButton.setAttribute('aria-label', 'Play Our Pick video');
            });
          } else video.pause();
        }
      }
      scheduleSync();
    }, { capture: true, signal });
    // Native AJAX submission is debounced. Prevent the browser default immediately,
    // but let the native delegated listener receive the event and own the request.
    root.addEventListener('submit', event => {
      if (event.target.id === 'CollectionFilterForm' && root.dataset.ajaxFiltering === 'true') event.preventDefault();
    }, { capture: true, signal });
    document.addEventListener('keydown', event => {
      if (!isOpen()) return;
      const panel = drawer();
      if (!panel) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.key === 'Tab') {
        const items = focusables(panel);
        const first = items[0];
        const last = items[items.length - 1];
        if (!first) {
          event.preventDefault();
          panel.focus();
        } else if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
          event.preventDefault();
          first.focus();
        }
      }
    }, { signal });
    document.addEventListener('focusin', event => {
      if (!isOpen()) return;
      const panel = drawer();
      if (!panel) return;
      if (panel.contains(event.target)) focusId = event.target.id || '';
      else (panel.querySelector('.filters__close') || panel).focus({ preventScroll: true });
    }, { signal });
    compactMobile.addEventListener('change', scheduleSync, { signal });
    const observer = new MutationObserver(scheduleSync);
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    controllers.set(root, () => {
      destroyed = true;
      observer.disconnect();
      events.abort();
      releaseBackground();
      if (previousOpen) document.body.classList.remove('ab-v9-filters-open');
      controllers.delete(root);
    });
    sync();
  }
  function initWithin(scope) {
    if (scope.matches && scope.matches(selector)) init(scope);
    scope.querySelectorAll(selector).forEach(init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initWithin(document), { once: true });
  else initWithin(document);
  document.addEventListener('shopify:section:load', event => initWithin(event.target));
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach((destroy, root) => {
      if (root === event.target || event.target.contains(root)) destroy();
    });
  });
})();
